import React, { useState, useEffect } from "react";
import styled from "styled-components/macro";
import { useHistory, useLocation } from "react-router-dom";
import { apiEndPointCrpt } from "../../env";
import { AuthUtil } from "../auth/AuthUtil";
import Loading from "../components/Loading";
import { convertToCelsius } from "../../utils/convertToCelsius";
import { convertToHpa } from "../../utils/convertToHpa";
import { savePDF } from "@progress/kendo-react-pdf";
import { drawDOM, exportPDF } from "@progress/kendo-drawing";
import {
    Typography,
    Grid,
    Divider,
    Box,
    Paper,
    useTheme,
    useMediaQuery,
    Tabs,
    Tab,
    Button,
} from "@material-ui/core";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    ReferenceLine,
    ResponsiveContainer,
} from "recharts";
import { GetApp } from "@material-ui/icons";
import {
    roomType,
    testFloor,
    buildingType, structureType, mitigationSystem,
    testPurpose,
} from "../../constants/values";

// ==========================================
// DESKTOP STYLED COMPONENTS
// ==========================================
const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #f5f5f5;
  padding: 40px 20px;
  min-height: 100vh;
  -webkit-text-size-adjust: 100% !important;
  text-size-adjust: 100% !important;

  @media print {
    background-color: white;
    padding: 0;
  }
`;

const A4Paper = styled(Paper)`
  width: 210mm;
  height: 297mm;
  padding: 10mm 15mm;
  margin-bottom: 30px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  background: white;
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media print {
    margin-bottom: 0;
    box-shadow: none;
    page-break-after: always;
    &:last-child {
      page-break-after: avoid;
    }
  }
`;

const TopBar = styled.div`
  width: 210mm;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  box-sizing: border-box;
  padding: 0 4px;

  @media print {
    display: none;
  }
`;

const BreadcrumbContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "Nunito";
  font-size: 17px;
  font-weight: 400;
  color: #64748b;
`;

const BreadcrumbLink = styled.span`
  color: #64748b;
  cursor: pointer;
  transition: color 0.2s;
  &:hover {
    color: #64748b;
    text-decoration: underline;
  }
`;

const BreadcrumbCurrent = styled.span`
  color: #475569;
  font-weight: 500;
`;

const DownloadButton = styled(Button)`
  text-transform: none !important;
  border: 1px solid #cfd8dc !important;
  border-radius: 6px !important;
  background-color: white !important;
  color: #546e7a !important;
  padding: 6px 14px !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  box-shadow: none !important;
  display: flex !important;
  align-items: center !important;
  gap: 8px !important;

  &:hover {
    background-color: #f5f7f8 !important;
    border-color: #b0bec5 !important;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 12px;
`;

const Title = styled(Typography)`
  font-weight: 900 !important;
  font-size: 28px !important;
  color: #0f172a;
  font-family: "Arial", "Roboto", "Helvetica", "Arial", sans-serif;
  letter-spacing: -0.5px;
`;

const Subtitle = styled(Typography)`
  color: #64748b;
  font-size: 14px !important;
  font-family: "Arial", "Roboto", "Helvetica", "Arial", sans-serif !important;
`;

const ReportIdLabel = styled(Typography)`
  color: #94a3b8;
  font-size: 11px !important;
  text-align: right;
  font-family: "Arial", "Roboto", "Helvetica", "Arial", sans-serif !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ReportId = styled(Typography)`
  font-weight: 800 !important;
  font-size: 16px !important;
  text-align: right;
  color: #0f172a;
  font-family: "Arial", "Roboto", "Helvetica", "Arial", sans-serif !important;
`;

const TagRow = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 8px;
  margin-top: 16px;
  margin-bottom: 20px;
  width: 100%;
`;

const Tag = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 5px 12px;
  font-size: 10px;
  color: #64748b;
  font-weight: 500;
  background-color: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  white-space: nowrap;
  font-family: "Arial", "Roboto", "Helvetica", "Arial", sans-serif;
`;

const BlueText = styled.span`
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const HighlightBoxWrapper = styled.div`
  background: ${props => props.isSafe
        ? "linear-gradient(90deg, #4CAF50 0%, #66BB6A 100%)"
        : "linear-gradient(90deg, #d32f2f 0%, #e53935 100%)"};
  border-radius: 12px;
  padding-top: 5px;
  margin-bottom: 20px;
`;

const HighlightBox = styled.div`
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  padding: 20px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
`;

const BoxLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const BoxRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 120px;
`;

const SectionTitle = styled(Typography)`
  font-weight: 800 !important;
  font-size: 14px !important;
  margin-top: 4px !important;
  margin-bottom: 14px !important;
  font-style: italic;
  color: #0f172a !important;
  font-family: "Arial", sans-serif !important;
`;

const InfoRow = styled(Grid)`
  margin-bottom: 8px !important;
`;

const InfoKey = styled(Typography)`
  font-size: 13px !important;
  color: #64748b !important;
  font-family: "Arial", sans-serif !important;
`;

const InfoValue = styled(Typography)`
  font-size: 13px !important;
  color: #0f172a !important;
  font-family: "Arial", sans-serif !important;
  font-weight: 500 !important;
`;

const InfoGrid = styled(Grid)`
  padding-left: 20px;
  box-sizing: border-box;
`;

const SectionDivider = styled(Divider)`
  margin: 16px 0 !important;
  background-color: #e2e8f0 !important;
`;

const Footer = styled.div`
  position: absolute;
  bottom: 15mm;
  left: 15mm;
  right: 15mm;
  display: flex;
  justify-content: space-between;
  border-top: 1px solid #eee;
  padding-top: 10px;
  color: #999;
  font-size: 12px;
`;

const EnvBox = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 10px;
  flex: 1;
`;

const EnvHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  font-weight: bold;
  font-size: 13px;
  font-style: italic;
`;

const EnvValueRow = styled.div`
  display: flex;
  justify-content: space-between;
  text-align: center;
  margin-top: 10px;
`;

const EnvValueCol = styled.div`
  display: flex;
  flex-direction: column;
`;

const EnvLabel = styled(Typography)`
  font-size: 11px !important;
  color: #888;
`;

const EnvValue = styled(Typography)`
  font-size: 14px !important;
  font-weight: bold !important;
  color: #000;
`;

const HourlyBox = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px 16px;
  background-color: #fcfcfc;
  font-size: 13px;
  color: #666;
`;

const BulletItem = styled.li`
  font-size: 13px;
  margin-bottom: 8px;
  color: #333;
`;

// ==========================================
// MOBILE STYLED COMPONENTS
// ==========================================
const MobileContainer = styled.div`
  background-color: #fafbfc;
  min-height: calc(100vh - 64px);
  padding: 24px 20px;
  font-family: "Arial", "Roboto", "Helvetica", "Arial", sans-serif;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  -webkit-text-size-adjust: 100% !important;
  text-size-adjust: 100% !important;
`;

const Breadcrumbs = styled(Typography)`
  color: #64748b;
  font-size: 13px !important;
  margin-bottom: 24px !important;
  font-family: "Arial", "Roboto", "Helvetica", "Arial", sans-serif;
`;

const MobileSummaryCard = styled(Paper)`
  background: #ffffff;
  border-radius: 16px !important;
  padding: 24px !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04) !important;
  border: 1px solid #e2e8f0;
  margin-bottom: 30px;
`;

const MobileSummaryTitle = styled(Typography)`
  font-size: 15px !important;
  font-weight: 700 !important;
  font-style: italic;
  color: #1e293b;
  margin-bottom: 24px !important;
`;

const MobileRowWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 12px;
  margin-bottom: 16px;
  border-bottom: 1.5px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const MobileRowLabel = styled(Typography)`
  font-size: 11px !important;
  font-weight: 500 !important;
  color: #94a3b8;
  text-transform: none;
  font-family: "Arial", "Roboto", "Helvetica", "Arial", sans-serif;
`;

const MobileRowValue = styled(Typography)`
  font-size: 14px !important;
  font-weight: 600 !important;
  color: #0f172a;
  margin-top: 4px !important;
  font-family: "Arial", "Roboto", "Helvetica", "Arial", sans-serif;
`;

const ButtonStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: auto;
  padding: 0 4px;
`;

const MobileOpenButton = styled(Button)`
  width: 100%;
  height: 48px;
  background-color: white !important;
  border: 1.5px solid #cbd5e1 !important;
  color: #334155 !important;
  font-size: 14px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 12px !important;
  box-shadow: none !important;
  transition: all 0.2s;

  &:hover {
    background-color: #f8fafc !important;
    border-color: #94a3b8 !important;
  }
`;

const MobileDownloadButton = styled(Button)`
  width: 100%;
  height: 48px;
  background-color: #29b6f6 !important;
  color: white !important;
  font-size: 14px !important;
  font-weight: 700 !important;
  text-transform: none !important;
  border-radius: 12px !important;
  box-shadow: 0 4px 12px rgba(41, 182, 246, 0.3) !important;
  transition: all 0.2s;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 8px !important;

  &:hover {
    background-color: #03a9f4 !important;
  }
`;

// ==========================================
// ==========================================



// Date Format Helpers
const formatDate = (ts) => {
    const date = new Date(ts);
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
};

const formatDateShortYear = (ts) => {
    const date = new Date(ts);
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const yy = String(date.getFullYear()).substring(2);
    return `${mm}/${dd}/${yy}`;
};

const formatDateShort = (ts) => {
    const date = new Date(ts);
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${months[date.getMonth()]} ${date.getDate()}`;
};



const formatDateTime = (ts) => {
    const date = new Date(ts);
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const yyyy = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    return `${mm}/${dd}/${yyyy} ${hours}:${minutes}${ampm}`;
};

const formatDateHyphen = (ts) => {
    const date = new Date(ts);
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${mm}-${dd}-${yyyy}`;
};



const exportAndSavePDF = async (element, options = {}) => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const fileName = options.fileName || "report.pdf";

    let newWindow = null;
    if (isSafari && !(isIOS || isMobileDevice)) {
        newWindow = window.open("about:blank", "_blank");
    }

    try {
        const drawOptions = {
            avoidLinks: options.avoidLinks,
            forcePageBreak: options.forcePageBreak,
            keepTogether: options.keepTogether,
            margin: options.margin,
            paperSize: options.paperSize,
            landscape: options.landscape,
            repeatHeaders: options.repeatHeaders,
            scale: options.scale,
        };

        const pdfOptions = {
            author: options.author,
            creator: options.creator || 'KendoReact PDF Generator',
            date: options.date,
            imgDPI: options.imageResolution,
            keywords: options.keywords,
            landscape: options.landscape,
            margin: options.margin,
            multiPage: true,
            paperSize: options.paperSize,
            producer: options.producer,
            subject: options.subject,
            title: options.title
        };

        const group = await drawDOM(element, drawOptions);
        const dataUri = await exportPDF(group, pdfOptions);

        const base64Parts = dataUri.split(";base64,");
        const contentType = base64Parts[0].split(":")[1] || "application/pdf";
        const raw = window.atob(base64Parts[1]);
        const rawLength = raw.length;
        const uInt8Array = new Uint8Array(rawLength);
        for (let i = 0; i < rawLength; ++i) {
            uInt8Array[i] = raw.charCodeAt(i);
        }
        // Use application/octet-stream on mobile/iOS devices to force a direct download prompt instead of opening in viewer
        const blobType = (isIOS || isMobileDevice) ? "application/octet-stream" : contentType;
        const blob = new Blob([uInt8Array], { type: blobType });

        const blobUrl = URL.createObjectURL(blob);

        if (isSafari && newWindow) {
            newWindow.location.href = blobUrl;
        } else {
            const a = document.createElement("a");
            a.style.display = "none";
            a.href = blobUrl;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
        }
    } catch (err) {
        if (newWindow) {
            newWindow.close();
        }
        throw err;
    }
};

// ==========================================
// DESKTOP VIEW COMPONENT
// ==========================================
function DesktopView({ reportId, isOffScreen, reportData }) {
    const history = useHistory();
    const location = useLocation();
    const [isDownloading, setIsDownloading] = useState(false);

    const countryCode = reportData?.property?.location?.countryCode?.trim().toUpperCase() || "US";
    const isUs = countryCode === "US";
    const tempUnit = isUs ? "°F" : "°C";
    const pressureUnit = isUs ? "inHg" : "hPa";
    const isAboveActionLevel = !isUs
        ? ((reportData?.analysis_radon?.overall_avg || 0) * 37 >= 200)
        : ((reportData?.analysis_radon?.overall_avg || 0) >= 4);
    const actionLevelText = !isUs ? "Health Canada Action Level" : "EPA Action Level";
    const radonUnit = !isUs ? "Bq/m3" : "pCi/L";

    const formatRadonValue = (value) => {
        if (value === undefined || value === null) return "N/A";
        if (!isUs) {
            return `${Math.round(value * 37)} Bq/m3`;
        }
        return `${value.toFixed(1)} pCi/L`;
    };

    const recommendationsDetails = !isUs ? [
        "Your average radon level is above the Health Canada guideline of 200 Bq/m³.",
        "Radon levels naturally vary by season, weather, and home ventilation conditions. Continued long-term monitoring can help you better understand your home’s radon patterns over time.",
        "Health Canada recommends taking corrective action when long-term radon levels exceed 200 Bq/m³. Consider consulting a certified radon professional to discuss mitigation options and additional testing recommendations.",
    ] : [
        "Your overall average is above the EPA action level of 4.0 pCi/L.",
        "Continue periodic monitoring; radon levels naturally vary by season.",
        "Consider a long-term (90+ day) test annually for a fuller picture.",
        "If a future short-term test shows ≥ 4.0 pCi/L, consult a certified radon professional."
    ];

    const trendData = reportData?.collected ? reportData.collected.map((entry) => ({
        name: formatDateShort(entry.timestamp),
        radon: !isUs ? Math.round(entry.radon * 37) : entry.radon,
        pressure: entry.pressure,
    })) : [];

    const envChartData = reportData?.collected ? reportData.collected.map((entry) => ({
        temp: entry.temp,
        hum: entry.hum,
        pres: entry.pressure,
    })) : [];

    const handleDownloadPDF = async () => {
        setIsDownloading(true);
        try {
            if (document.fonts && !document.fonts.__overridden__) {
                Object.defineProperty(document.fonts, "ready", {
                    value: Promise.resolve(),
                    configurable: true,
                    writable: true,
                });
                document.fonts.__overridden__ = true;
                console.log("document.fonts.ready overridden successfully to resolve immediately");
            }
        } catch (e) {
            console.warn("Failed to override document.fonts.ready:", e);
        }

        const element = document.getElementById("pdf-report-content");
        if (element) {
            console.log("Calling exportAndSavePDF on real report element:", element);
            try {
                const finalFileName = (reportId && typeof reportId === "string" && reportId.startsWith("RPT"))
                    ? `${reportId}.pdf`
                    : `${reportData?.report_id || "report"}.pdf`;
                await exportAndSavePDF(element, {
                    scale: 0.7,
                    paperSize: "Letter",
                    fileName: finalFileName,
                    margin: 0,
                    forcePageBreak: ".pdf-page-break",
                });
                console.log("exportAndSavePDF called successfully with filename:", finalFileName);
            } catch (err) {
                alert("PDF export error: " + err.message);
                console.error("PDF export error details:", err);
            }
        } else {
            alert("Error: pdf-report-content element not found!");
        }
        setIsDownloading(false);
    };

    return (
        <PageContainer>
            {!isOffScreen && (
                <TopBar>
                    <BreadcrumbContainer>
                        <BreadcrumbLink onClick={() => history.push(location.pathname.includes("2") || location.pathname.includes("myreports") || location.pathname.includes("radon") ? "/myreports" : "/reports")}>
                            My Reports
                        </BreadcrumbLink>
                        <span>/</span>
                        <BreadcrumbCurrent>
                            {reportData?.test_info?.session_name || "Session"}
                        </BreadcrumbCurrent>
                    </BreadcrumbContainer>
                    <DownloadButton
                        variant="outlined"
                        startIcon={isDownloading ? null : <GetApp />}
                        onClick={handleDownloadPDF}
                        disabled={isDownloading}
                    >
                        {isDownloading ? "Generating PDF..." : "Download PDF"}
                    </DownloadButton>
                </TopBar>
            )}

            <div id="pdf-report-content">
                {/* PAGE 1 */}
                <A4Paper elevation={isOffScreen ? 0 : 3}>
                    <HeaderRow>
                        <div>
                            <Title>Ecosense Radon Report</Title>
                            <Subtitle>{reportData?.device?.name || "EcoQube"} - Continuous Radon Monitor</Subtitle>
                        </div>
                        <div>
                            <ReportIdLabel>Report ID</ReportIdLabel>
                            <ReportId>{reportData?.report_id || reportId}</ReportId>
                        </div>
                    </HeaderRow>

                    <TagRow>
                        <Tag>
                            <BlueText>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2196f3" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                    <polyline points="22 4 12 14.01 9 11.01" />
                                </svg>
                                {!isUs ? "CNRPP-Listed Device" : "NRPP-Listed Device"}
                            </BlueText>
                        </Tag>
                        <Tag>
                            <BlueText>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2196f3" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                    <polyline points="14 2 14 8 20 8" />
                                    <line x1="16" y1="13" x2="8" y2="13" />
                                    <line x1="16" y1="17" x2="8" y2="17" />
                                </svg>
                                {!isUs ? "HC-Protocol" : "EPA-Protocol"}
                            </BlueText>
                        </Tag>
                        <Tag>
                            <BlueText>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2196f3" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                                </svg>
                                {!isUs ? "Electronic Radon Monitor (ERM)" : "Continuous Radon Monitor (CRM)"}
                            </BlueText>
                        </Tag>
                        <Tag>
                            <BlueText>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2196f3" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                                    <polyline points="9 11 11 13 15 9" />
                                </svg>
                                Calibrated - exp {formatDateShortYear(reportData?.device?.calibration_expiry_date)}
                            </BlueText>
                        </Tag>
                    </TagRow>

                    <HighlightBoxWrapper isSafe={!isAboveActionLevel}>
                        <HighlightBox>
                            <BoxLeft>
                                <Typography
                                    style={{
                                        color: isAboveActionLevel ? "#d32f2f" : "#4CAF50",
                                        fontWeight: "bold",
                                        fontSize: 22,
                                        fontFamily: '"Arial", sans-serif',
                                    }}
                                >
                                    {isAboveActionLevel ? `≥ ${actionLevelText}` : `< ${actionLevelText}`}
                                </Typography>
                                <Typography
                                    style={{
                                        fontSize: 11,
                                        color: "#666",
                                        maxWidth: 240,
                                        lineHeight: 1.4,
                                        fontFamily: '"Arial", sans-serif',
                                    }}
                                >
                                    {!isUs
                                        ? "Health Canada recommends mitigation if the radon average is 200 Bq/m3 or more"
                                        : "EPA recommends mitigation if the radon average is 4pCi/L or more"}
                                </Typography>
                            </BoxLeft>
                            <BoxRight>
                                <Typography
                                    style={{
                                        fontSize: 28,
                                        fontWeight: "bold",
                                        color: "#000",
                                        lineHeight: 1.1,
                                        fontFamily: '"Arial", sans-serif',
                                    }}
                                >
                                    {formatRadonValue(reportData?.analysis_radon?.overall_avg)}
                                </Typography>
                                <Typography
                                    style={{
                                        fontSize: 12,
                                        color: "#666",
                                        fontWeight: 500,
                                        fontFamily: '"Arial", sans-serif',
                                        marginTop: 2,
                                    }}
                                >
                                    Radon Average
                                </Typography>
                            </BoxRight>
                        </HighlightBox>
                    </HighlightBoxWrapper>

                    <SectionTitle>Property Information</SectionTitle>
                    <InfoGrid container>
                        <InfoRow item xs={12} container>
                            <Grid item xs={4}>
                                <InfoKey>Address</InfoKey>
                            </Grid>
                            <Grid item xs={8}>
                                <InfoValue>
                                    {`${reportData?.property?.location?.street || ""} ${reportData?.property?.location?.city || ""} ${reportData?.property?.location?.state || ""}`}
                                </InfoValue>
                            </Grid>
                        </InfoRow>
                        <InfoRow item xs={12} container>
                            <Grid item xs={4}>
                                <InfoKey>Building Type</InfoKey>
                            </Grid>
                            <Grid item xs={8}>
                                <InfoValue>{reportData?.property?.building_type || "N/A"}</InfoValue>
                            </Grid>
                        </InfoRow>
                        <InfoRow item xs={12} container>
                            <Grid item xs={4}>
                                <InfoKey>Structure Type</InfoKey>
                            </Grid>
                            <Grid item xs={8}>
                                <InfoValue>{reportData?.property?.structure_type || "N/A"}</InfoValue>
                            </Grid>
                        </InfoRow>
                        <InfoRow item xs={12} container>
                            <Grid item xs={4}>
                                <InfoKey>Mitigation System</InfoKey>
                            </Grid>
                            <Grid item xs={8}>
                                <InfoValue>{reportData?.property?.mitigation_installed || "N/A"}</InfoValue>
                            </Grid>
                        </InfoRow>
                        <InfoRow item xs={12} container>
                            <Grid item xs={4}>
                                <InfoKey>Year Built</InfoKey>
                            </Grid>
                            <Grid item xs={8}>
                                <InfoValue>{reportData?.property?.year_built || "N/A"}</InfoValue>
                            </Grid>
                        </InfoRow>
                    </InfoGrid>

                    <SectionDivider />

                    <SectionTitle>Test Information</SectionTitle>
                    <InfoGrid container>
                        <InfoRow item xs={12} container>
                            <Grid item xs={4}>
                                <InfoKey>Session Name</InfoKey>
                            </Grid>
                            <Grid item xs={8}>
                                <InfoValue>{reportData?.test_info?.session_name || "N/A"}</InfoValue>
                            </Grid>
                        </InfoRow>
                        <InfoRow item xs={12} container>
                            <Grid item xs={4}>
                                <InfoKey>Test Duration</InfoKey>
                            </Grid>
                            <Grid item xs={8}>
                                <InfoValue>{reportData?.test_info?.duration_display || `${reportData?.test_info?.duration_days || 0} Days`}</InfoValue>
                            </Grid>
                        </InfoRow>
                        <InfoRow item xs={12} container>
                            <Grid item xs={4}>
                                <InfoKey>Test Floor</InfoKey>
                            </Grid>
                            <Grid item xs={8}>
                                <InfoValue>{reportData?.test_info?.test_floor || "N/A"}</InfoValue>
                            </Grid>
                        </InfoRow>
                        <InfoRow item xs={12} container>
                            <Grid item xs={4}>
                                <InfoKey>Room Type</InfoKey>
                            </Grid>
                            <Grid item xs={8}>
                                <InfoValue>{reportData?.test_info?.room_type || "N/A"}</InfoValue>
                            </Grid>
                        </InfoRow>
                        <InfoRow item xs={12} container>
                            <Grid item xs={4}>
                                <InfoKey>Test Started</InfoKey>
                            </Grid>
                            <Grid item xs={8}>
                                <InfoValue>{formatDateTime(reportData?.test_info?.start_time)}</InfoValue>
                            </Grid>
                        </InfoRow>
                        <InfoRow item xs={12} container>
                            <Grid item xs={4}>
                                <InfoKey>Test Finished</InfoKey>
                            </Grid>
                            <Grid item xs={8}>
                                <InfoValue>{formatDateTime(reportData?.test_info?.end_time)}</InfoValue>
                            </Grid>
                        </InfoRow>
                    </InfoGrid>

                    <SectionDivider />

                    <SectionTitle>Device Information</SectionTitle>
                    <InfoGrid container>
                        <InfoRow item xs={12} container>
                            <Grid item xs={4}>
                                <InfoKey>Device Name</InfoKey>
                            </Grid>
                            <Grid item xs={8}>
                                <InfoValue>{reportData?.device?.name || "EcoQube"}</InfoValue>
                            </Grid>
                        </InfoRow>
                        <InfoRow item xs={12} container>
                            <Grid item xs={4}>
                                <InfoKey>Model Number</InfoKey>
                            </Grid>
                            <Grid item xs={8}>
                                <InfoValue>{reportData?.device?.model_number || "N/A"}</InfoValue>
                            </Grid>
                        </InfoRow>
                        <InfoRow item xs={12} container>
                            <Grid item xs={4}>
                                <InfoKey>Serial Number</InfoKey>
                            </Grid>
                            <Grid item xs={8}>
                                <InfoValue>{reportData?.device?.serial_number || "N/A"}</InfoValue>
                            </Grid>
                        </InfoRow>
                        <InfoRow item xs={12} container>
                            <Grid item xs={4}>
                                <InfoKey>Manufacturer</InfoKey>
                            </Grid>
                            <Grid item xs={8}>
                                <InfoValue>{reportData?.device?.manufacturer || "Ecosense"}</InfoValue>
                            </Grid>
                        </InfoRow>
                        <InfoRow item xs={12} container>
                            <Grid item xs={4}>
                                <InfoKey>Calibration Exp. Date</InfoKey>
                            </Grid>
                            <Grid item xs={8}>
                                <InfoValue>{formatDate(reportData?.device?.calibration_expiry_date)}</InfoValue>
                            </Grid>
                        </InfoRow>
                    </InfoGrid>

                    <Footer>
                        <span>Report ID: {reportData?.report_id || reportId} - Generated {formatDateHyphen(reportData?.generated_date)}</span>
                        <span>Page 1 of 3</span>
                    </Footer>
                </A4Paper>

                {/* PAGE 2 */}
                <A4Paper elevation={3} className="pdf-page-break">
                    <SectionTitle style={{ marginTop: 0 }}>
                        Daily Radon Trend
                    </SectionTitle>
                    <Typography style={{ fontSize: 12, color: "#888", marginBottom: 20 }}>
                        Daily averages over the 91-day long-term test (07/08/2025 –
                        10/07/2025). Min/max statistics below reflect hourly readings.
                    </Typography>

                    <Box style={{ height: 180, width: "100%", marginBottom: 10, display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <LineChart
                            width={650}
                            height={180}
                            data={trendData}
                            margin={{ top: 25, right: 0, left: 0, bottom: 0 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis
                                dataKey="name"
                                tick={{ fontSize: 10 }}
                                tickLine={false}
                                axisLine={false}
                            />
                            <YAxis
                                yAxisId="left"
                                tick={{ fontSize: 10 }}
                                tickLine={false}
                                axisLine={false}
                                domain={!isUs ? [0, 300] : [0, 8]}
                                label={{
                                    value: `(${radonUnit})`,
                                    position: "top",
                                    offset: 12,
                                    dy: 0,
                                    dx: 10,
                                    fontSize: 10,
                                    fill: "#888",
                                }}
                            />
                            <YAxis
                                yAxisId="right"
                                orientation="right"
                                tick={{ fontSize: 10 }}
                                tickLine={false}
                                axisLine={false}
                                domain={isUs ? [28, 32] : [950, 1050]}
                                label={{
                                    value: `(${pressureUnit})`,
                                    position: "top",
                                    offset: 12,
                                    dy: 0,
                                    dx: -10,
                                    fontSize: 10,
                                    fill: "#888",
                                }}
                            />
                            <ReferenceLine
                                y={!isUs ? 200 : 4}
                                yAxisId="left"
                                stroke="#f44336"
                                strokeDasharray="3 3"
                                label={{
                                    position: "left",
                                    value: !isUs ? "HC 200" : "EPA 4.0",
                                    fill: "#f44336",
                                    fontSize: 10,
                                    dx: -15,
                                }}
                            />
                            <Line
                                yAxisId="left"
                                type="monotone"
                                dataKey="radon"
                                stroke="#2196F3"
                                dot={false}
                                strokeWidth={2}
                                name={`Radon (${radonUnit})`}
                            />
                            <Line
                                yAxisId="right"
                                type="monotone"
                                dataKey="pressure"
                                stroke="#9C27B0"
                                strokeDasharray="5 5"
                                dot={false}
                                strokeWidth={1}
                                name={`Pressure (${pressureUnit})`}
                            />
                        </LineChart>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                gap: 20,
                                marginTop: 10,
                            }}
                        >
                            <Typography
                                style={{ fontSize: 11, display: "flex", alignItems: "center" }}
                            >
                                <span
                                    style={{
                                        display: "inline-block",
                                        width: 20,
                                        height: 2,
                                        backgroundColor: "#2196F3",
                                        marginRight: 5,
                                    }}
                                ></span>{" "}
                                Radon ({radonUnit})
                            </Typography>
                            <Typography
                                style={{ fontSize: 11, display: "flex", alignItems: "center" }}
                            >
                                <span
                                    style={{
                                        display: "inline-block",
                                        width: 20,
                                        height: 2,
                                        borderBottom: "1px dashed #9C27B0",
                                        marginRight: 5,
                                    }}
                                ></span>{" "}
                                Pressure ({pressureUnit})
                            </Typography>
                            <Typography
                                style={{ fontSize: 11, display: "flex", alignItems: "center" }}
                            >
                                <span
                                    style={{
                                        display: "inline-block",
                                        width: 20,
                                        height: 2,
                                        borderBottom: "1px dotted #f44336",
                                        marginRight: 5,
                                    }}
                                ></span>{" "}
                                {actionLevelText}
                            </Typography>
                        </div>
                    </Box>

                    <SectionDivider />

                    <SectionTitle>Radon Concentration</SectionTitle>
                    <Grid container>
                        <InfoRow item xs={12} container>
                            <Grid item xs={6}>
                                <InfoKey>Measurement Radon Concentration Max</InfoKey>
                            </Grid>
                            <Grid item xs={6}>
                                <InfoValue>{formatRadonValue(reportData?.analysis_radon?.highest)}</InfoValue>
                            </Grid>
                        </InfoRow>
                        <InfoRow item xs={12} container>
                            <Grid item xs={6}>
                                <InfoKey>Measurement Radon Concentration Average</InfoKey>
                            </Grid>
                            <Grid item xs={6}>
                                <InfoValue>{formatRadonValue(reportData?.analysis_radon?.overall_avg)}</InfoValue>
                            </Grid>
                        </InfoRow>
                        <InfoRow item xs={12} container>
                            <Grid item xs={6}>
                                <InfoKey>Measurement Radon Concentration Min</InfoKey>
                            </Grid>
                            <Grid item xs={6}>
                                <InfoValue>{formatRadonValue(reportData?.analysis_radon?.lowest)}</InfoValue>
                            </Grid>
                        </InfoRow>
                        <InfoRow item xs={12} container>
                            <Grid item xs={6}>
                                <InfoKey>% Uncertainty</InfoKey>
                            </Grid>
                            <Grid item xs={6}>
                                <InfoValue>± {(reportData?.analysis_radon?.uncertainty_percentage || 1.4).toFixed(1)}%</InfoValue>
                            </Grid>
                        </InfoRow>
                    </Grid>

                    <SectionDivider />

                    <SectionTitle>Environmental Conditions ({reportData?.device?.name || "EcoQube"})</SectionTitle>
                    <Typography style={{ fontSize: 12, color: "#888", marginBottom: 16 }}>
                        Min / avg / max temperature, humidity, and barometric pressure
                        reflect hourly readings across the test window.
                    </Typography>

                    <div style={{ display: "flex", gap: 16, marginBottom: 10 }}>
                        {/* Temperature */}
                        <EnvBox>
                            <EnvHeader>
                                <span>Temperature</span>
                                <span style={{ color: "#888", fontWeight: "normal" }}>{tempUnit}</span>
                            </EnvHeader>
                            <Box style={{ height: 40, width: "100%", display: "flex", justifyContent: "center" }}>
                                <LineChart width={196} height={40} data={envChartData}>
                                    <Line
                                        type="monotone"
                                        dataKey="temp"
                                        stroke="#FF9800"
                                        dot={false}
                                        strokeWidth={2}
                                    />
                                </LineChart>
                            </Box>
                            <EnvValueRow>
                                <EnvValueCol>
                                    <EnvLabel>MIN</EnvLabel>
                                    <EnvValue>{reportData?.environmental_stats?.temperature?.min ?? "N/A"}</EnvValue>
                                </EnvValueCol>
                                <EnvValueCol>
                                    <EnvLabel>AVG</EnvLabel>
                                    <EnvValue>{reportData?.environmental_stats?.temperature?.avg ?? "N/A"}</EnvValue>
                                </EnvValueCol>
                                <EnvValueCol>
                                    <EnvLabel>MAX</EnvLabel>
                                    <EnvValue>{reportData?.environmental_stats?.temperature?.max ?? "N/A"}</EnvValue>
                                </EnvValueCol>
                            </EnvValueRow>
                        </EnvBox>

                        {/* Humidity */}
                        <EnvBox>
                            <EnvHeader>
                                <span>Humidity</span>
                                <span style={{ color: "#888", fontWeight: "normal" }}>
                                    % RH
                                </span>
                            </EnvHeader>
                            <Box style={{ height: 40, width: "100%", display: "flex", justifyContent: "center" }}>
                                <LineChart width={196} height={40} data={envChartData}>
                                    <Line
                                        type="monotone"
                                        dataKey="hum"
                                        stroke="#03A9F4"
                                        dot={false}
                                        strokeWidth={2}
                                    />
                                </LineChart>
                            </Box>
                            <EnvValueRow>
                                <EnvValueCol>
                                    <EnvLabel>MIN</EnvLabel>
                                    <EnvValue>{reportData?.environmental_stats?.humidity?.min ?? "N/A"}</EnvValue>
                                </EnvValueCol>
                                <EnvValueCol>
                                    <EnvLabel>AVG</EnvLabel>
                                    <EnvValue>{reportData?.environmental_stats?.humidity?.avg ?? "N/A"}</EnvValue>
                                </EnvValueCol>
                                <EnvValueCol>
                                    <EnvLabel>MAX</EnvLabel>
                                    <EnvValue>{reportData?.environmental_stats?.humidity?.max ?? "N/A"}</EnvValue>
                                </EnvValueCol>
                            </EnvValueRow>
                        </EnvBox>

                        {/* Pressure */}
                        <EnvBox>
                            <EnvHeader>
                                <span>Pressure</span>
                                <span style={{ color: "#888", fontWeight: "normal" }}>{pressureUnit}</span>
                            </EnvHeader>
                            <Box style={{ height: 40, width: "100%", display: "flex", justifyContent: "center" }}>
                                <LineChart width={196} height={40} data={envChartData}>
                                    <Line
                                        type="monotone"
                                        dataKey="pres"
                                        stroke="#673AB7"
                                        dot={false}
                                        strokeWidth={2}
                                    />
                                </LineChart>
                            </Box>
                            <EnvValueRow>
                                <EnvValueCol>
                                    <EnvLabel>MIN</EnvLabel>
                                    <EnvValue>{reportData?.environmental_stats?.pressure?.min ?? "N/A"}</EnvValue>
                                </EnvValueCol>
                                <EnvValueCol>
                                    <EnvLabel>AVG</EnvLabel>
                                    <EnvValue>{reportData?.environmental_stats?.pressure?.avg ?? "N/A"}</EnvValue>
                                </EnvValueCol>
                                <EnvValueCol>
                                    <EnvLabel>MAX</EnvLabel>
                                    <EnvValue>{reportData?.environmental_stats?.pressure?.max ?? "N/A"}</EnvValue>
                                </EnvValueCol>
                            </EnvValueRow>
                        </EnvBox>
                    </div>

                    <SectionDivider />

                    <SectionTitle>Hourly Measurement Data</SectionTitle>
                    <HourlyBox>
                        Hourly radon, temperature, humidity, and pressure readings are
                        available in the <strong>Ecosense app</strong> under this session.
                        The certified report summarizes daily averages to communicate
                        long-term exposure for the {reportData?.test_info?.duration_adjective || `${reportData?.test_info?.duration_days || 0}-day`} test.
                    </HourlyBox>

                    <Footer>
                        <span>Report ID: {reportData?.report_id || reportId} - Generated {formatDateHyphen(reportData?.generated_date)}</span>
                        <span>Page 2 of 3</span>
                    </Footer>
                </A4Paper>
                {/* PAGE 3 */}
                <A4Paper elevation={3} className="pdf-page-break">
                    <SectionTitle style={{ marginTop: 0 }}>
                        Recommended Actions
                    </SectionTitle>
                    <ul style={{ paddingLeft: 20, margin: 0, marginBottom: 10 }}>
                        {recommendationsDetails.map((detail, idx) => (
                            <BulletItem key={idx}>{detail}</BulletItem>
                        ))}
                    </ul>

                    <SectionDivider />

                    <SectionTitle>Methodology & Protocol</SectionTitle>
                    <Typography
                        style={{
                            fontSize: 13,
                            color: "#666",
                            marginBottom: 10,
                            lineHeight: 1.5,
                        }}
                    >
                        {!isUs
                            ? "This report is based on long-term continuous radon measurements collected using a C-NRPP / NRPP-approved radon monitoring device. Measurements were recorded over a 30-day or 91-day monitoring period under normal living conditions. Hourly radon concentration data was analyzed to calculate average radon levels and trends throughout the measurement period. Reported results may include normal environmental variations caused by weather, ventilation, occupancy, HVAC operation, and seasonal changes."
                            : "This report is based on long-term continuous radon measurements collected using an NRPP-approved radon monitoring device. Measurements were recorded over a 30-day or 91-day monitoring period under normal living conditions. Hourly radon concentration data was analyzed to calculate average radon levels and trends throughout the measurement period. Reported results may include normal environmental variations caused by weather, ventilation, occupancy, HVAC operation, and seasonal changes."
                        }
                    </Typography>

                    <SectionDivider />

                    <SectionTitle>Test Limitation & Disclaimer</SectionTitle>
                    <Typography
                        style={{
                            fontSize: 13,
                            color: "#666",
                            marginBottom: 10,
                            lineHeight: 1.5,
                        }}
                    >
                        {!isUs
                            ? "All radon measurements include some level of uncertainty due to statistical variation and changing environmental conditions. Radon concentrations can fluctuate daily and seasonally based on weather, ventilation, occupancy, and building operation. Temperature and humidity measurements may also vary depending on environmental conditions.This report is generated using data collected from a continuous radon monitoring device and is intended for informational and educational purposes. Some provinces or local jurisdictions may require radon testing performed by a certified radon measurement professional for real-estate or regulatory transactions. Please verify local requirements before relying on this report for official purposes."
                            : "All radon measurements include some level of uncertainty due to statistical variation and changing environmental conditions. Radon concentrations can fluctuate daily and seasonally based on weather, ventilation, occupancy, and building operation. Temperature and humidity measurements may also vary depending on environmental conditions.This report is generated using data collected from an NRPP-approved continuous radon monitoring device and is intended for informational and educational purposes. Some states or local jurisdictions may require radon testing performed by a licensed radon measurement professional for real-estate or regulatory transactions. Please verify local requirements before relying on this report for official purposes."
                        }
                    </Typography>
                    <Typography style={{ fontSize: 13, color: "#666", lineHeight: 1.5 }}>
                        This consumer-generated report is provided for informational use.
                        Some jurisdictions require a licensed radon measurement professional
                        to conduct tests used in real-estate transactions — please verify
                        local requirements.
                    </Typography>

                    <Footer>
                        <span>Report ID: {reportData?.report_id || reportId} - Generated {formatDateHyphen(reportData?.generated_date)}</span>
                        <span>Page 3 of 3</span>
                    </Footer>
                </A4Paper>
            </div>
        </PageContainer>
    );
}

// ==========================================
// MOBILE VIEW COMPONENT
// ==========================================
function MobileView({ reportId, reportData }) {
    const history = useHistory();
    const location = useLocation();
    const [showFullReport, setShowFullReport] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    const countryCode = reportData?.property?.location?.countryCode?.trim().toUpperCase() || "US";
    const isUs = countryCode === "US";
    const isAboveActionLevel = !isUs
        ? ((reportData?.analysis_radon?.overall_avg || 0) * 37 >= 200)
        : ((reportData?.analysis_radon?.overall_avg || 0) >= 4);
    const actionLevelText = !isUs ? "Health Canada Action Level" : "EPA Action Level";
    const radonUnit = !isUs ? "Bq/m3" : "pCi/L";

    const formatRadonValue = (value) => {
        if (value === undefined || value === null) return "N/A";
        if (!isUs) {
            return `${Math.round(value * 37)} Bq/m3`;
        }
        return `${value.toFixed(1)} pCi/L`;
    };

    const recommendationsDetails = !isUs ? [
        "Your average radon level is above the Health Canada guideline of 200 Bq/m³.",
        "Radon levels naturally vary by season, weather, and home ventilation conditions. Continued long-term monitoring can help you better understand your home’s radon patterns over time.",
        "Health Canada recommends taking corrective action when long-term radon levels exceed 200 Bq/m³. Consider consulting a certified radon professional to discuss mitigation options and additional testing recommendations.",
    ] : [
        "Your overall average is above the EPA action level of 4.0 pCi/L.",
        "Continue periodic monitoring; radon levels naturally vary by season.",
        "Consider a long-term (90+ day) test annually for a fuller picture.",
        "If a future short-term test shows ≥ 4.0 pCi/L, consult a certified radon professional."
    ];

    const trendData = reportData?.collected ? reportData.collected.map((entry) => ({
        name: formatDateShort(entry.timestamp),
        radon: !isUs ? Math.round(entry.radon * 37) : entry.radon,
        pressure: entry.pressure,
    })) : [];

    const envChartData = reportData?.collected ? reportData.collected.map((entry) => ({
        temp: entry.temp,
        hum: entry.hum,
        pres: entry.pressure,
    })) : [];

    const handleDownloadPDF = async () => {
        setIsDownloading(true);
        try {
            if (document.fonts && !document.fonts.__overridden__) {
                Object.defineProperty(document.fonts, "ready", {
                    value: Promise.resolve(),
                    configurable: true,
                    writable: true,
                });
                document.fonts.__overridden__ = true;
                console.log("document.fonts.ready overridden successfully to resolve immediately");
            }
        } catch (e) {
            console.warn("Failed to override document.fonts.ready:", e);
        }

        const element = document.getElementById("pdf-report-content");
        if (element) {
            console.log("Calling exportAndSavePDF on mobile element:", element);
            try {
                const finalFileName = (reportId && typeof reportId === "string" && reportId.startsWith("RPT"))
                    ? `${reportId}.pdf`
                    : `${reportData?.report_id || "report"}.pdf`;
                await exportAndSavePDF(element, {
                    scale: 0.75,
                    paperSize: "A4",
                    fileName: finalFileName,
                    margin: 0,
                    forcePageBreak: ".pdf-page-break",
                });
                console.log("exportAndSavePDF called successfully with filename:", finalFileName);
            } catch (err) {
                alert("PDF export error: " + err.message);
                console.error("PDF export error details:", err);
            }
        } else {
            alert("Error: pdf-report-content element not found!");
        }
        setIsDownloading(false);
    };

    if (showFullReport) {
        return (
            <div style={{ backgroundColor: "#f5f5f5", minHeight: "100vh", padding: "16px" }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom="16px">
                    <Button
                        variant="outlined"
                        onClick={() => setShowFullReport(false)}
                        style={{ textTransform: "none", backgroundColor: "white", borderColor: "#cbd5e1", borderRadius: 8 }}
                    >
                        ← Back
                    </Button>
                    <Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
                        Detailed Report Preview
                    </Typography>
                </Box>
                <div style={{ overflowX: "auto", width: "100%", paddingBottom: "20px" }}>
                    <div style={{ width: "210mm" }}>
                        <DesktopView reportId={reportId} isOffScreen={false} reportData={reportData} />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <MobileContainer>
            <BreadcrumbContainer style={{ fontSize: 15, marginBottom: 20 }}>
                <BreadcrumbLink onClick={() => history.push(location.pathname.includes("2") || location.pathname.includes("myreports") || location.pathname.includes("radon") ? "/myreports" : "/reports")}>
                    My Reports
                </BreadcrumbLink>
                <span>/</span>
                <BreadcrumbCurrent>
                    {reportData?.test_info?.session_name || "Session"}
                </BreadcrumbCurrent>
            </BreadcrumbContainer>

            <MobileSummaryCard>
                <MobileSummaryTitle>Ecosense Radon Report Summary</MobileSummaryTitle>

                <MobileRowWrapper>
                    <MobileRowLabel>Session Name</MobileRowLabel>
                    <MobileRowValue>{reportData?.test_info?.session_name || "N/A"}</MobileRowValue>
                </MobileRowWrapper>

                <MobileRowWrapper>
                    <MobileRowLabel>Radon Average</MobileRowLabel>
                    <MobileRowValue>{formatRadonValue(reportData?.analysis_radon?.overall_avg)}</MobileRowValue>
                </MobileRowWrapper>

                <MobileRowWrapper>
                    <MobileRowLabel>Test Period</MobileRowLabel>
                    <MobileRowValue>
                        {`${formatDate(reportData?.test_info?.start_time)} - ${formatDate(reportData?.test_info?.end_time)}`}
                    </MobileRowValue>
                </MobileRowWrapper>

                <MobileRowWrapper>
                    <MobileRowLabel>Test Floor</MobileRowLabel>
                    <MobileRowValue>{reportData?.test_info?.test_floor || "N/A"}</MobileRowValue>
                </MobileRowWrapper>

                <MobileRowWrapper>
                    <MobileRowLabel>Requested</MobileRowLabel>
                    <MobileRowValue>{formatDate(reportData?.generated_date)}</MobileRowValue>
                </MobileRowWrapper>
            </MobileSummaryCard>

            <ButtonStack>
                <MobileDownloadButton variant="contained" onClick={handleDownloadPDF} disabled={isDownloading}>
                    {isDownloading ? null : <GetApp style={{ fontSize: 20 }} />}
                    {isDownloading ? "Generating PDF..." : "Download PDF Report"}
                </MobileDownloadButton>
            </ButtonStack>
        </MobileContainer>
    );
}

const parseApiReportData = (apiData) => {
    if (!apiData) return null;

    let item = apiData.data || apiData.result || apiData;
    if (Array.isArray(item)) {
        item = item[0];
    }

    if (!item) return null;

    const parseTimestamp = (ts) => {
        if (!ts) return 0;
        if (typeof ts === "number") {
            return ts < 1e11 ? ts * 1000 : ts;
        }
        const num = Number(ts);
        if (!isNaN(num) && num > 0) {
            return num < 1e11 ? num * 1000 : num;
        }
        const parsed = new Date(ts).getTime();
        return isNaN(parsed) ? 0 : parsed;
    };

    const rawRadondata = item.radondata || [];

    console.log(`[Timestamp Debug] Total raw radondata entries: ${rawRadondata.length}`);
    rawRadondata.slice(0, 30).forEach((entry, i) => {
        const rawTs = entry.timestamp;
        const parsedMs = parseTimestamp(rawTs);
        const dateStr = new Date(parsedMs).toLocaleString();
        let diffSec = 0;
        let diffHours = "0";
        if (i > 0) {
            const prevMs = parseTimestamp(rawRadondata[i - 1].timestamp);
            diffSec = (parsedMs - prevMs) / 1000;
            diffHours = (diffSec / 3600).toFixed(2);
        }
        console.log(`[Timestamp Log #${i}] raw: ${rawTs} -> ${dateStr} | increment: +${diffSec}s (+${diffHours}h)`);
    });

    const country = item.device_location?.country || "US";
    const countryCode = (item.device_location?.countryCode || country).trim().toUpperCase();
    const isUs = countryCode === "US";

    const radondata = [...rawRadondata].sort((a, b) => {
        return parseTimestamp(a.timestamp) - parseTimestamp(b.timestamp);
    });

    const temps = radondata
        .map(r => (r.tmp !== null && r.tmp !== undefined ? (isUs ? parseFloat(r.tmp) : convertToCelsius(r.tmp, 0)) : null))
        .filter(t => t !== null && !isNaN(t));
    const hums = radondata
        .map(r => (r.hmd !== null && r.hmd !== undefined ? parseFloat(r.hmd) : null))
        .filter(h => h !== null && !isNaN(h));
    const pressures = radondata
        .map(r => (r.prs !== null && r.prs !== undefined ? (isUs ? parseFloat(r.prs) : convertToHpa(r.prs)) : null))
        .filter(p => p !== null && !isNaN(p));

    const getStats = (arr, defaultStats, decimals = 0) => {
        if (arr.length === 0) return defaultStats;
        const min = Math.min(...arr);
        const max = Math.max(...arr);
        const avg = arr.reduce((a, b) => a + b, 0) / arr.length;
        if (decimals > 0) {
            return {
                min: Number(min.toFixed(decimals)),
                avg: Number(avg.toFixed(decimals)),
                max: Number(max.toFixed(decimals))
            };
        }
        return {
            min: Math.round(min),
            avg: Math.round(avg),
            max: Math.round(max)
        };
    };

    const temperatureStats = getStats(temps, isUs ? { min: 59, avg: 69, max: 72 } : { min: 15, avg: 21, max: 22 });
    const humidityStats = getStats(hums, { min: 47, avg: 50, max: 62 });
    const pressureStats = getStats(
        pressures,
        isUs ? { min: 29.74, avg: 29.92, max: 30.03 } : { min: 1007, avg: 1013, max: 1017 },
        isUs ? 2 : 0
    );

    const parseDateToMs = (dateInput, fallbackMs) => {
        if (!dateInput && dateInput !== 0) return fallbackMs;
        if (typeof dateInput === "number") {
            return dateInput < 1e11 ? dateInput * 1000 : dateInput;
        }
        if (typeof dateInput === "string") {
            const str = dateInput.trim();
            if (!str) return fallbackMs;
            const formattedStr = str.includes(" ") && !str.includes("T") ? str.replace(" ", "T") : str;
            const parsedMs = new Date(formattedStr).getTime();
            if (!isNaN(parsedMs) && parsedMs > 0) {
                return parsedMs;
            }
            const num = Number(str);
            if (!isNaN(num) && num > 0) {
                return num < 1e11 ? num * 1000 : num;
            }
        }
        return fallbackMs;
    };

    const rawStartTime = item.measurement_start_d || item.start_d || item.start_time;
    const rawEndTime = item.measurement_end_d || item.end_d || item.end_time;
    const rawGenTime = item.generated_date || item.created_at || item.created;

    const fallbackEnd = rawGenTime ? parseDateToMs(rawGenTime, Date.now()) : Date.now();
    const fallbackStart = item.measurement_duration ? fallbackEnd - item.measurement_duration * 1000 : fallbackEnd - 86400000;

    const startTime = parseDateToMs(rawStartTime, fallbackStart);
    const endTime = parseDateToMs(rawEndTime, fallbackEnd);
    const generatedTime = parseDateToMs(rawGenTime, fallbackEnd);

    const durationSeconds = item.measurement_duration
        ? Number(item.measurement_duration)
        : (startTime && endTime && endTime > startTime ? (endTime - startTime) / 1000 : 86400);

    const isLessThanOneDay = durationSeconds < 86400;
    const durationHours = Math.round(durationSeconds / 3600);
    const durationDays = Math.round(durationSeconds / 86400);

    const durationDisplay = isLessThanOneDay
        ? `${durationHours || 1} Hours`
        : `${durationDays || 1} Days`;

    const durationAdjective = isLessThanOneDay
        ? `${durationHours || 1}-hour`
        : `${durationDays || 1}-day`;

    const avgRadonBq = item.avg_radon || 0;
    const avgRadonPci = parseFloat((avgRadonBq / 37).toFixed(2));
    const highestRadonPci = item.highest_radon != null ? parseFloat((item.highest_radon / 37).toFixed(2)) : 0;
    const lowestRadonPci = item.lowest_radon != null ? parseFloat((item.lowest_radon / 37).toFixed(2)) : 0;

    const actionRecommended = !isUs ? (avgRadonBq >= 200) : (avgRadonPci >= 4.0);

    const getCalibrationExpiryDate = (calDateInput, fallbackTime) => {
        const rawTime = calDateInput
            ? (typeof calDateInput === "number" && calDateInput < 1e11 ? calDateInput * 1000 : calDateInput)
            : fallbackTime;
        const dateObj = new Date(rawTime);
        if (!isNaN(dateObj.getTime())) {
            dateObj.setFullYear(dateObj.getFullYear() + 1);
            return dateObj.getTime();
        }
        const fallbackDate = new Date(fallbackTime);
        fallbackDate.setFullYear(fallbackDate.getFullYear() + 1);
        return fallbackDate.getTime();
    };

    const calibrationExpiryDate = getCalibrationExpiryDate(item.calibration_date, endTime);

    return {
        report_id: item.sn_report_start,
        generated_date: generatedTime,

        property: {
            location: {
                street: item.device_location?.street || "",
                city: item.device_location?.city || "",
                state: item.device_location?.state || "",
                zipCode: item.device_location?.zipcode || "",
                countryCode: item.device_location?.countryCode || "US",
                country: country
            },
            building_type: buildingType[item.device_placement?.bld_type],
            structure_type: structureType[item.device_placement?.str_type],
            mitigation_installed: mitigationSystem[item.device_placement?.mitigation_installed],
            year_built: item.device_placement?.year_built || "N/A"
        },

        test_info: {
            session_name: item.device_placement?.session_name,
            room_type: roomType[item.device_placement?.room_type],
            test_floor: testFloor[item.device_placement?.location_floor],
            duration_days: durationDays || 1,
            duration_hours: durationHours || 1,
            duration_display: durationDisplay,
            duration_adjective: durationAdjective,
            start_time: startTime,
            end_time: endTime
        },

        device: {
            name: "EcoQube Apex",
            model_number: "EA200",
            serial_number: item.serial_number,
            manufacturer: "Ecosense(FTLab)",
            calibration_expiry_date: calibrationExpiryDate
        },

        analysis_radon: {
            overall_avg: avgRadonPci,
            epa_avg: avgRadonPci,
            highest: highestRadonPci,
            lowest: lowestRadonPci,
            uncertainty_percentage: 1.4
        },

        environmental_stats: {
            temperature: temperatureStats,
            humidity: humidityStats,
            pressure: pressureStats
        },

        recommendations: {
            headline: actionRecommended ? "Action Recommended" : "No Action Required",
            details: !isUs ? [
                "Your average radon level is above the Health Canada guideline of 200 Bq/m³.",
                "Radon levels naturally vary by season, weather, and home ventilation conditions. Continued long-term monitoring can help you better understand your home’s radon patterns over time.",
                "Health Canada recommends taking corrective action when long-term radon levels exceed 200 Bq/m³. Consider consulting a certified radon professional to discuss mitigation options and additional testing recommendations.",
            ] : [
                "Your overall average is above the EPA action level of 4.0 pCi/L.",
                "Continue periodic monitoring; radon levels naturally vary by season.",
                "Consider a long-term (90+ day) test annually for a fuller picture.",
                "If a future short-term test shows ≥ 4.0 pCi/L, consult a certified radon professional."
            ]
        },

        collected: radondata.map(entry => {
            const ts = parseTimestamp(entry.timestamp);
            const radonPci = entry.radon_level != null ? parseFloat((entry.radon_level / 37).toFixed(2)) : 0;
            const tempVal = entry.tmp != null && entry.tmp !== "" ? (isUs ? parseFloat(entry.tmp) : convertToCelsius(entry.tmp, 0)) : 0;
            const prsVal = entry.prs != null && entry.prs !== "" ? (isUs ? parseFloat(entry.prs) : convertToHpa(entry.prs)) : 0;
            return {
                timestamp: ts,
                radon: radonPci,
                pressure: prsVal,
                temp: tempVal,
                hum: entry.hmd ? parseFloat(entry.hmd) : 0
            };
        })
    };
};

export default function ReportEcosense() {
    const theme = useTheme();
    // Use MUI's useMediaQuery to determine if screen size is sm or smaller (mobile)
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const queryReportId = searchParams.get("reportId") || searchParams.get("report_id");
    const { reportId, email } = location.state || {};
    const currentReportId = reportId || queryReportId || "RPT-252954";
    const currentEmail = email || "";

    const [reportData, setReportData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let isMounted = true;
        const fetchReportData = async () => {
            try {
                setIsLoading(true);
                setError(null);
                let token = "";
                try {
                    token = await AuthUtil.getAuthToken();
                } catch (authErr) {
                    console.warn("Auth token retrieval failed, proceeding without Authorization header:", authErr);
                }

                const queryParams = new URLSearchParams();
                if (currentReportId) {
                    queryParams.append("sn_report_start", currentReportId);
                }
                //const requestUrl = `${apiEndPointCrpt}/report/demo${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
                const requestUrl = `${apiEndPointCrpt}/report${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;


                const response = await fetch(requestUrl, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        ...(token ? { Authorization: token } : {}),
                    },
                });
                if (!response.ok) {
                    throw new Error(`Failed to fetch report: ${response.status}`);
                }
                const data = await response.json();
                if (isMounted) {
                    console.log("Fetched raw report data:", data);
                    const parsedData = parseApiReportData(data);
                    if (!parsedData) {
                        throw new Error("Invalid or empty report data received from API");
                    }
                    console.log("Parsed reportData:", parsedData);
                    setReportData(parsedData);
                    setIsLoading(false);
                }
            } catch (err) {
                console.error("Error fetching report details from demo API:", err);
                if (isMounted) {
                    setError(err.message);
                    setIsLoading(false);
                }
            }
        };

        fetchReportData();
        return () => {
            isMounted = false;
        };
    }, [currentReportId, currentEmail]);

    if (isLoading) {
        return <Loading />;
    }

    if (error) {
        return (
            <div style={{ padding: 40, textAlign: "center", color: "#ef4444" }}>
                <h3>Error loading report</h3>
                <p>{error}</p>
            </div>
        );
    }

    if (isMobile) {
        return (
            <>
                <MobileView reportId={currentReportId} reportData={reportData} />
                {/* Render DesktopView off-screen just for PDF download on mobile */}
                <div style={{ position: "absolute", left: "-9999px", top: "-9999px", WebkitTextSizeAdjust: "100%", textSizeAdjust: "100%" }}>
                    <DesktopView reportId={currentReportId} isOffScreen={true} reportData={reportData} />
                </div>
            </>
        );
    }

    return <DesktopView reportId={currentReportId} reportData={reportData} />;
}
